import { debounce } from "lodash-es";
import { Plugin, PluginKey } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";

import { schema } from "../adapter/schema";
import { isLinkCreateFormOpen, openLinkPopoverForExistingLink } from "../toolbar/link";
import { markActive } from "../utils";

const DEBOUNCE_DELAY_MS = 100; // debounce delay to prevent flickering on rapid selection changes

interface LinkPopoverState {
  isOpen: boolean;
}

/** Metadata key for updating the popover close function. */
export const UPDATE_POPOVER_CLOSE_META = "updateLinkPopoverClose";

/** Creates a plugin that opens/closes a popover when the cursor enters/leaves linked text. */
export function linkPopoverPlugin(): Plugin<LinkPopoverState> {
  const pluginKey = new PluginKey<LinkPopoverState>("linkPopover");
  const markType = schema.marks.link;
  if (!markType) {
    // Return a no-op plugin if link mark doesn't exist in the schema
    return new Plugin<LinkPopoverState>({
      key: pluginKey,
      state: {
        init() {
          return { isOpen: false };
        },
        apply(tr, value) {
          return value;
        },
      },
    });
  }

  let closePopover: (() => void) | null = null;

  const _maybeClosePopover = () => {
    if (closePopover) {
      closePopover();
      closePopover = null;
    }
  };

  return new Plugin<LinkPopoverState>({
    key: pluginKey,

    state: {
      init() {
        return { isOpen: false };
      },

      apply(tr, value) {
        // Check for metadata updates to the close function
        const closeFnUpdate = tr.getMeta(UPDATE_POPOVER_CLOSE_META) as (() => void) | undefined;
        if (closeFnUpdate !== undefined) {
          _maybeClosePopover();
          closePopover = closeFnUpdate;
        }

        if (tr.selectionSet) {
          return { isOpen: closePopover !== null };
        }
        return value;
      },
    },
    view(view: EditorView) {
      const handleSelectionChange = debounce(() => {
        const state = view.state;
        const isInLink = markActive(state, markType);
        const isEmpty = state.selection.empty;

        _maybeClosePopover();

        // Only handle empty selections (cursor, not text selection)
        if (!isEmpty) return;

        if (isInLink && !isLinkCreateFormOpen()) {
          // Always create a new popover so that the value can be updated
          // if necessary while the user is moving the cursor within the link.
          const close = openLinkPopoverForExistingLink(view, markType);
          closePopover = close;
        }
      }, DEBOUNCE_DELAY_MS);

      return {
        update(updatedView: EditorView, prevState) {
          // Only handle selection changes, not document changes
          if (updatedView.state.selection !== prevState.selection) {
            handleSelectionChange();
          }
        },
        destroy() {
          handleSelectionChange.cancel();
          _maybeClosePopover();
        },
      };
    },
  });
}
